// AddVendorPurchaseModal.jsx
import { useContext, useState } from "react";
import axios from "axios";
import { X, Loader2, Calendar, IndianRupee } from "lucide-react";
import { toast } from "react-toastify";
import MyContext from "../../../MyContext";

// Same auth as VendorManager
const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";

// New v2 API endpoint
const PURCHASE_ADD_V2 = "/api/Inventory/v2.0/insertVendorPurchaseDetails";

// Payment mode options (update labels/values if backend expects different)
const PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "imps", label: "IMPS / Bank Transfer" },
];

export default function AddVendorPurchaseModal({
  isOpen,
  onClose,
  userID,
  onSuccess, // optional callback (e.g. refresh purchase list)
}) {
  const { baseUrl } = useContext(MyContext);

  const today = new Date().toISOString().slice(0, 10);

  const [vendorName, setVendorName] = useState("");
  const [amount, setAmount] = useState("");
  const [purchaseDescription, setPurchaseDescription] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(today);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES[0].value);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  function validate() {
    if (!userID) return "Missing userid";
    if (!vendorName.trim()) return "Vendor name is required";
    if (!amount) return "Amount is required";
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Enter a valid amount (max 2 decimals)";
    if (Number(amount) <= 0) return "Amount must be greater than 0";
    if (!purchaseDate) return "Purchase date is required";
    if (!paymentMode) return "Payment mode is required";
    return null;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    const err = validate();
    if (err) {
      toast.warning(err);
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        userid: String(userID),
        vendorName: vendorName.trim(),
        amount: String(amount),
        purchaseDescription: String(purchaseDescription || ""),
        purchaseDate: String(purchaseDate),
        paymentMode: String(paymentMode),
      };

      const res = await axios.post(`${baseUrl}${PURCHASE_ADD_V2}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
      });

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Purchase added");

        // Reset form
        setVendorName("");
        setAmount("");
        setPurchaseDescription("");
        setPurchaseDate(today);
        setPaymentMode(PAYMENT_MODES[0].value);

        onClose?.();
        onSuccess?.(); // e.g. refresh purchase history
      } else {
        toast.error(res?.data?.responseMessage || "Failed to add purchase");
      }
    } catch (error) {
      console.error(error);
      toast.error("API error while adding purchase");
    } finally {
      setSubmitting(false);
    }
  }

  function handleBackdropClick() {
    if (!submitting) onClose?.();
  }

  return (
    <div className="admin-modal-backdrop" onClick={handleBackdropClick}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h4 className="admin-drawer-title">Add Purchase</h4>
          <button className="admin-icon-btn" onClick={handleBackdropClick}>
            <X size={18} />
          </button>
        </div>

        <form
          className="admin-drawer-body admin-drawer-form admin-drawer-scroll"
          onSubmit={handleSubmit}
        >
          {/* Vendor Name */}
          <div className="admin-field">
            <label>
              Vendor Name <span className="req">*</span>
            </label>
            <input
              className="admin-input-control"
              placeholder="Enter vendor name"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              required
            />
          </div>

          {/* Amount */}
          <div className="admin-field">
            <label>
              Amount <span className="req">*</span>
            </label>
            <input
              className="admin-input-control mono"
              inputMode="decimal"
              placeholder="e.g., 1500 or 1500.50"
              value={amount}
              onChange={(e) =>
                setAmount(
                  e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1")
                    .slice(0, 12)
                )
              }
            />
          </div>

          {/* Description (textarea) */}
          <div className="admin-field">
            <label>Description</label>
            <textarea
              className="admin-input-control"
              rows={3}
              placeholder="Optional purchase description"
              value={purchaseDescription}
              onChange={(e) => setPurchaseDescription(e.target.value)}
            />
          </div>

          {/* Purchase Date */}
          <div className="admin-field">
            <label>
              Purchase Date <span className="req">*</span>
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Calendar size={16} />
              <input
                type="date"
                className="admin-input-control"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </div>
          </div>

          {/* Payment Mode (select) */}
          <div className="admin-field">
            <label>
              Payment Mode <span className="req">*</span>
            </label>
            <select
              className="admin-input-control"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-modal-actions">
            <button
              type="button"
              className="admin-btn admin-btn-neutral btn--md"
              onClick={handleBackdropClick}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary btn--md"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="admin-spin" size={16} /> : <IndianRupee size={16} />}
              <span style={{ marginLeft: 6 }}>
                {submitting ? "Saving…" : "Add Purchase"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
